import Clickable from 'components/Common/Clickable'
import Pane from 'components/Common/Pane'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import RadioButton from 'LuxKit/Radio/RadioButton'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import { rem } from 'polished'
import { useCallback } from 'react'
import * as React from 'react'
import styled, { css } from 'styled-components'
import noop from 'lib/function/noop'
import { useIntl } from 'react-intl'
import { getIllustrativePurposesOnlyLabel } from 'lib/flights/sharedFlightLabels'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import CharterRewardGreatValueBadge from './CharterRewardGreatValueBadge'

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  flex: 1;
  min-height: 0;
`

const OptionsSlot = styled(VerticalSpacer)<{ $hasMultipleOptions: boolean }>`
  & {
    min-height: ${props => props.$hasMultipleOptions ? rem(148) : 0};
  }
`

const FareOptionButton = styled(Clickable)`
  display: block;
  width: 100%;

  &:focus-visible {
    outline: ${rem(2)} solid ${props => props.theme.palette.highlight.primary.normalForeground};
    outline-offset: ${rem(2)};
    border-radius: ${props => props.theme.borderRadius.S};
  }
`

const rewardFareOptionLayout = css`
  justify-content: flex-start;
  min-height: ${rem(64)};
  padding: ${rem(24)} ${rem(12)} ${rem(10)};
`

const cashFareOptionLayout = css`
  justify-content: center;
  min-height: ${rem(48)};
  padding-block: ${rem(8)};
  padding-inline: ${rem(12)} ${rem(44)};
`

const FareOptionPane = styled.div<{ $selected?: boolean, $disabled?: boolean, $reward?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  ${props => props.$reward ? rewardFareOptionLayout : cashFareOptionLayout}
  border: 1px solid ${props => props.$selected ?
    props.theme.palette.neutral.default.one :
    props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: ${props => props.$disabled ?
    props.theme.palette.neutral.default.seven :
    props.theme.palette.neutral.default.eight};
`

const RadioSlot = styled.div`
  position: absolute;
  top: 50%;
  inset-inline-end: ${rem(12)};
  transform: translateY(-50%);
`

const FullHeightPane = styled(Pane)`
  height: 100%;
  width: 100%;
  min-width: ${rem(260)};
`

const DisclaimerLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.six};
`

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  min-height: ${rem(20)};
  white-space: nowrap;
`

const RewardHeadingRow = styled(Group)`
  padding-inline-end: ${rem(32)};
`

export interface FareClassCardOption {
  fareId: string
  subtitle: React.ReactNode
  isSelected: boolean
  disabled?: boolean
  isRewardOption?: boolean
}

interface Props {
  name: string
  imageId: string
  options: Array<FareClassCardOption>
  onSelect: (fareId: string) => void
}

function FareClassCard({
  name,
  imageId,
  options,
  onSelect,
  children,
}: React.PropsWithChildren<Props>) {
  const intl = useIntl()
  const illustrativePurposesOnlyLabel = getIllustrativePurposesOnlyLabel(intl)
  const onOptionClick = useCallback((fareId: string, disabled?: boolean) => {
    if (!disabled) {
      onSelect(fareId)
    }
  }, [onSelect])

  const societeRewardsSubline = intl.formatMessage(
    {
      id: 'oDGwSeNRaY8VwqgIVTRP',
      defaultMessage: '{loyaltyProgramName} Rewards',
    },
    { loyaltyProgramName: getLoyaltyProgramDisplayName() },
  )

  return <CardContainer>
    <FullHeightPane
      border={options.some(option => option.isSelected) ? 'neutral-one' : 'neutral-five'}
      padding="32 16 24 16"
      background="neutral-eight"
    >
      <VerticalSpacer gap={16}>
        <Heading variant="heading6">{name}</Heading>
        <OptionsSlot gap={8} $hasMultipleOptions={options.length > 1}>
          {options.map(option => (
            <FareOptionButton
              key={option.fareId}
              onClick={option.disabled ? noop : () => onOptionClick(option.fareId, option.disabled)}
              aria-pressed={option.isSelected}
              aria-disabled={option.disabled || undefined}
            >
              <FareOptionPane $selected={option.isSelected} $disabled={option.disabled} $reward={option.isRewardOption}>
                {option.isRewardOption && <CharterRewardGreatValueBadge />}
                <VerticalSpacer gap={4}>
                  <Group direction="horizontal" gap={8} horizontalAlign="start" verticalAlign="center">
                    {option.isRewardOption && <RewardHeadingRow direction="horizontal" gap={8} verticalAlign="center">
                      <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
                      <BodyText variant="small" weight="bold" colour="highlight-primary-dark">
                        {societeRewardsSubline}
                      </BodyText>
                    </RewardHeadingRow>}
                    {!option.isRewardOption && <PriceRow>{option.subtitle}</PriceRow>}
                  </Group>
                  {option.isRewardOption && <PriceRow>{option.subtitle}</PriceRow>}
                </VerticalSpacer>
                <RadioSlot>
                  <RadioButton checked={option.isSelected} disabled={option.disabled} />
                </RadioSlot>
              </FareOptionPane>
            </FareOptionButton>
          ))}
        </OptionsSlot>
        <div>
          <ResponsiveImage
            id={imageId}
            aspectRatio="218:143"
            desktopAspectRatio="309:174"
          />
          <DisclaimerLabel>
            <Caption variant="medium" colour="neutral-two">
              {illustrativePurposesOnlyLabel}
            </Caption>
          </DisclaimerLabel>
        </div>
        <div>
          {children}
        </div>
      </VerticalSpacer>
    </FullHeightPane>
  </CardContainer>
}

export default FareClassCard
